import { appInsights } from "./appInsights";
import { APP_KPI_NAME } from "./utils";

const STORAGE_KEY = "pcs_login_pending";
const MAX_PENDING_LOGIN_MS = 30 * 60 * 1000;

export interface LoginTimingContext {
  module?: string;
  screen?: string;
  action?: string;
  role?: string;
  path?: string;
  trigger?: string;
  completedPath?: string;
  completedModule?: string;
  completedScreen?: string;
  loginId?: string;
}

type PendingLogin = LoginTimingContext & {
  loginId: string;
  startedAtMs: number;
  externalAuthStartedAtMs?: number;
};

export type LoginStageProperties = Pick<LoginTimingContext, "path" | "trigger"> & {
  verificationFlow?: string;
};

export type LoginStageTimer = {
  complete: (properties?: LoginStageProperties) => boolean;
  fail: (error?: unknown, properties?: LoginStageProperties) => boolean;
};

const DEFAULT_CONTEXT = {
  module: "authentication",
  screen: "Login",
  action: "login",
};

let memoryPendingLogin: PendingLogin | null = null;

function createLoginId(): string {
  try {
    const randomUUID = globalThis.crypto?.randomUUID;
    if (typeof randomUUID === "function") {
      const loginId = randomUUID.call(globalThis.crypto);
      if (loginId) return loginId;
    }
  } catch {
    // Fall through to a compatibility-safe identifier.
  }

  try {
    const values = new Uint32Array(4);
    globalThis.crypto?.getRandomValues?.(values);
    if (values.some((value) => value !== 0)) {
      return `login-${Array.from(values)
        .map((value) => value.toString(16).padStart(8, "0"))
        .join("")}`;
    }
  } catch {
    // Fall through to the final local-only fallback.
  }

  return `login-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function nowMs(): number {
  if (typeof performance === "undefined") return Date.now();

  const origin =
    typeof performance.timeOrigin === "number"
      ? performance.timeOrigin
      : Date.now() - performance.now();

  return origin + performance.now();
}

function getStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function removeStorageItem(storage: Storage, key: string) {
  try {
    storage.removeItem(key);
  } catch {
    // Storage failures must never affect authentication.
  }
}

function readPendingLogin(): PendingLogin | null {
  if (memoryPendingLogin) return memoryPendingLogin;

  const storage = getStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<PendingLogin>;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof parsed.startedAtMs !== "number" ||
      !Number.isFinite(parsed.startedAtMs) ||
      !parsed.startedAtMs ||
      nowMs() - parsed.startedAtMs > MAX_PENDING_LOGIN_MS
    ) {
      removeStorageItem(storage, STORAGE_KEY);
      return null;
    }

    const pending = {
      ...parsed,
      loginId:
        typeof parsed.loginId === "string" && parsed.loginId.trim()
          ? parsed.loginId
          : createLoginId(),
    } as PendingLogin;

    memoryPendingLogin = pending;
    if (!(typeof parsed.loginId === "string" && parsed.loginId.trim())) {
      writePendingLogin(pending);
    }
    return pending;
  } catch {
    removeStorageItem(storage, STORAGE_KEY);
    return null;
  }
}

function writePendingLogin(pending: PendingLogin) {
  memoryPendingLogin = pending;

  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // Telemetry should never break authentication.
  }
}

function clearPendingLogin() {
  memoryPendingLogin = null;

  const storage = getStorage();
  if (!storage) return;

  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Telemetry should never break authentication.
  }
}

function safeTrackMetric(metric: Parameters<typeof appInsights.trackMetric>[0]) {
  try {
    appInsights.trackMetric(metric);
  } catch {
    // Telemetry must never become part of authentication control flow.
  }
}

function safeTrackEvent(event: Parameters<typeof appInsights.trackEvent>[0]) {
  try {
    appInsights.trackEvent(event);
  } catch {
    // Telemetry must never become part of authentication control flow.
  }
}

function getErrorMessage(error: unknown): string {
  if (error && typeof error === "object") {
    if ("message" in error) {
      return String((error as { message?: unknown }).message ?? "Unknown error");
    }
    if ("error" in error) {
      return String((error as { error?: unknown }).error ?? "Unknown error");
    }
  }

  return typeof error === "string" && error.trim() ? error : "Unknown error";
}

function buildProperties(
  pending: PendingLogin,
  durationMs: number,
  success: boolean,
  context?: LoginTimingContext,
  error?: unknown,
) {
  const merged = { ...DEFAULT_CONTEXT, ...pending, ...context };

  return {
    loginId: pending.loginId,
    module: merged.module,
    screen: merged.screen,
    action: merged.action,
    role: merged.role ?? "",
    path: merged.path ?? "",
    trigger: merged.trigger ?? "",
    completedPath: merged.completedPath ?? "",
    completedModule: merged.completedModule ?? "",
    completedScreen: merged.completedScreen ?? "",
    success,
    durationMs,
    ...(success ? {} : { errorMessage: getErrorMessage(error) }),
  };
}

function trackLoginResult(
  pending: PendingLogin,
  success: boolean,
  context?: LoginTimingContext,
  error?: unknown,
) {
  const durationMs = Math.max(0, nowMs() - pending.startedAtMs);
  const properties = buildProperties(pending, durationMs, success, context, error);

  safeTrackMetric({
    name: APP_KPI_NAME.PCS_LOGIN,
    average: durationMs,
    sampleCount: 1,
    properties: {
      ...properties,
      success: String(success),
      durationMs: String(durationMs),
    } as never,
  });

  safeTrackEvent({
    name: success ? "pcs_login_completed" : "pcs_login_failed",
    properties,
  });
}

function getPerformanceNow(): number {
  try {
    return typeof performance === "undefined" ? Date.now() : performance.now();
  } catch {
    return Date.now();
  }
}

function getSafeErrorType(error: unknown): string {
  if (error instanceof Error && error.name) return error.name;
  if (error === null) return "null";
  return typeof error;
}

function buildStageProperties(
  pending: PendingLogin,
  stage: string,
  durationMs: number,
  success: boolean,
  context: LoginStageProperties = {},
  error?: unknown,
) {
  const path = context.path ?? pending.path;
  const trigger = context.trigger ?? pending.trigger;
  const properties: Record<string, unknown> = {
    loginId: pending.loginId,
    stage,
    success,
    durationMs,
  };

  if (path) properties.path = path;
  if (trigger) properties.trigger = trigger;
  if (context.verificationFlow) properties.verificationFlow = context.verificationFlow;
  if (!success) properties.errorType = getSafeErrorType(error);

  return properties;
}

function trackLoginStageResult(
  pending: PendingLogin,
  stage: string,
  startedAt: number,
  success: boolean,
  context: LoginStageProperties = {},
  error?: unknown,
) {
  const durationMs = Math.max(0, getPerformanceNow() - startedAt);
  const properties = buildStageProperties(pending, stage, durationMs, success, context, error);

  safeTrackMetric({
    name: APP_KPI_NAME.PCS_LOGIN_STAGE,
    average: durationMs,
    sampleCount: 1,
    properties: {
      ...properties,
      success: String(success),
      durationMs: String(durationMs),
    } as never,
  });

  safeTrackEvent({
    name: success ? "pcs_login_stage_completed" : "pcs_login_stage_failed",
    properties,
  });
}

function trackExternalAuthRoundtripResult(
  pending: PendingLogin,
  startedAt: number,
  success: boolean,
  error?: unknown,
) {
  const durationMs = Math.max(0, Date.now() - startedAt);
  const properties = buildStageProperties(
    pending,
    "external_auth_roundtrip",
    durationMs,
    success,
    {},
    error,
  );

  safeTrackMetric({
    name: APP_KPI_NAME.PCS_LOGIN_STAGE,
    average: durationMs,
    sampleCount: 1,
    properties: {
      ...properties,
      success: String(success),
      durationMs: String(durationMs),
    } as never,
  });

  safeTrackEvent({
    name: success ? "pcs_login_stage_completed" : "pcs_login_stage_failed",
    properties,
  });
}

export function startLoginTiming(context: LoginTimingContext = {}): boolean {
  try {
    const existing = readPendingLogin();
    if (existing) return false;

    writePendingLogin({
      ...DEFAULT_CONTEXT,
      ...context,
      loginId: createLoginId(),
      startedAtMs: nowMs(),
    });
    return true;
  } catch {
    return false;
  }
}

export function getPendingLoginId(): string | null {
  try {
    return readPendingLogin()?.loginId ?? null;
  } catch {
    return null;
  }
}

export function startExternalAuthRoundtrip(): boolean {
  try {
    const pending = readPendingLogin();
    if (
      !pending ||
      (typeof pending.externalAuthStartedAtMs === "number" &&
        Number.isFinite(pending.externalAuthStartedAtMs))
    ) {
      return false;
    }

    writePendingLogin({
      ...pending,
      externalAuthStartedAtMs: Date.now(),
    });
    return true;
  } catch {
    return false;
  }
}

function finishExternalAuthRoundtrip(success: boolean, error?: unknown): boolean {
  try {
    const pending = readPendingLogin();
    const startedAt = pending?.externalAuthStartedAtMs;
    if (!pending || typeof startedAt !== "number" || !Number.isFinite(startedAt)) return false;

    const pendingWithoutExternalStart = { ...pending };
    delete pendingWithoutExternalStart.externalAuthStartedAtMs;
    writePendingLogin(pendingWithoutExternalStart);
    trackExternalAuthRoundtripResult(pending, startedAt, success, error);
    return true;
  } catch {
    return false;
  }
}

export function completeExternalAuthRoundtrip(): boolean {
  return finishExternalAuthRoundtrip(true);
}

export function failExternalAuthRoundtrip(error: unknown): boolean {
  return finishExternalAuthRoundtrip(false, error);
}

export function startLoginStage(
  stage: string,
  properties: LoginStageProperties = {},
): LoginStageTimer | null {
  try {
    const pending = readPendingLogin();
    if (!pending) return null;

    const startedAt = getPerformanceNow();
    let finished = false;

    return {
      complete: (completionProperties = {}) => {
        if (finished) return false;
        finished = true;
        try {
          trackLoginStageResult(pending, stage, startedAt, true, {
            ...properties,
            ...completionProperties,
          });
        } catch {
          // A timing helper failure must never affect authentication.
        }
        return true;
      },
      fail: (error, completionProperties = {}) => {
        if (finished) return false;
        finished = true;
        try {
          trackLoginStageResult(
            pending,
            stage,
            startedAt,
            false,
            {
              ...properties,
              ...completionProperties,
            },
            error,
          );
        } catch {
          // A timing helper failure must never affect authentication.
        }
        return true;
      },
    };
  } catch {
    return null;
  }
}

export function completeLoginTiming(context: LoginTimingContext = {}): boolean {
  try {
    const pending = readPendingLogin();
    if (!pending) return false;

    clearPendingLogin();
    trackLoginResult(pending, true, context);
    return true;
  } catch {
    return false;
  }
}

export function failLoginTiming(error: unknown, context: LoginTimingContext = {}): boolean {
  try {
    const pending = readPendingLogin();
    if (!pending) return false;

    clearPendingLogin();
    trackLoginResult(pending, false, context, error);
    return true;
  } catch {
    return false;
  }
}

export function hasPendingLoginTiming(): boolean {
  try {
    return Boolean(readPendingLogin());
  } catch {
    return false;
  }
}

export function clearLoginTiming() {
  clearPendingLogin();
}
